import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/base/MovieCard";
import { hotKeywords, searchMedia } from "@/lib/media";
import { mediaList } from "@/mocks/movies";
import { usePageMeta } from "@/hooks/usePageMeta";

const fallbackPicks = [...mediaList].sort((a, b) => b.heat - a.heat).slice(0, 4);

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();
  const [input, setInput] = useState(keyword);

  usePageMeta({
    title: keyword ? `搜索“${keyword}” - 蜜桃33影视资源` : "影视搜索 - 蜜桃33影视资源",
    description: keyword
      ? `蜜桃33搜索“${keyword}”的影视结果，包含相关电影、电视剧与综艺的剧情介绍与推荐。`
      : "在蜜桃33搜索电影、电视剧与综艺，按片名、类型、地区或主创快速定位想看的内容。",
    keywords: keyword
      ? `蜜桃33,${keyword},影视搜索,影视资源`
      : "蜜桃33,影视搜索,电影搜索,电视剧搜索",
  });

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [keyword]);

  const results = searchMedia(keyword);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    if (value) setSearchParams({ q: value });
    else setSearchParams({});
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="w-full flex-1">
        <section className="w-full border-b border-background-200 bg-background-100 px-4 py-10 md:px-8 md:py-14">
          <div className="max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.3em] text-primary-600">Search library</p>
            <h1 className="mt-2 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              找一部想看的
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              支持按片名、类型、地区、导演或演员搜索。输入关键词后回车即可刷新结果。
            </p>

            <form onSubmit={submit} className="mt-6 flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
                <input
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  name="q"
                  aria-label="影视搜索关键词"
                  placeholder="试试搜索“悬疑”“家庭”或片名"
                  className="h-12 w-full rounded-md border border-background-300 bg-background-50 pl-11 pr-4 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="inline-flex h-12 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
                <i className="ri-arrow-right-line" />
              </button>
            </form>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-foreground-400">热门搜索</span>
              {hotKeywords.map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => setSearchParams({ q: word })}
                  className="cursor-pointer rounded-full border border-background-200 bg-background-50 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  {word}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 py-8 md:px-8 md:py-10">
          {keyword ? (
            <>
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-4">
                <p className="text-sm text-foreground-600">
                  关键词 <span className="font-medium text-foreground-950">“{keyword}”</span> 的搜索结果
                </p>
                <p className="text-xs text-foreground-500">
                  共找到 <span className="font-semibold text-primary-600">{results.length}</span> 部作品
                </p>
              </div>

              {results.length > 0 ? (
                <div className="grid grid-cols-2 gap-x-3 gap-y-7 sm:grid-cols-3 md:gap-x-5 md:gap-y-9 lg:grid-cols-4 xl:grid-cols-5">
                  {results.map((item) => (
                    <MovieCard key={item.id} item={item} />
                  ))}
                </div>
              ) : (
                <div>
                  <div className="rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-14 text-center">
                    <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-foreground-400">
                      <i className="ri-search-eye-line text-2xl" />
                    </span>
                    <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
                      没有找到与“{keyword}”相关的内容
                    </h2>
                    <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground-500">
                      换一个更短的关键词试试，或者从下面的热门作品里挑一部。也可以直接浏览分类页按条件筛选。
                    </p>
                    <div className="mt-5 flex flex-col justify-center gap-2 sm:flex-row">
                      <Link
                        to="/category/movie"
                        className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                      >
                        浏览电影专区
                      </Link>
                      <Link
                        to="/category/variety"
                        className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                      >
                        浏览综艺专区
                      </Link>
                    </div>
                  </div>

                  <div className="mt-10">
                    <h2 className="font-heading text-xl font-bold text-foreground-950">大家都在看</h2>
                    <div className="mt-5 grid grid-cols-2 gap-x-3 gap-y-7 sm:grid-cols-3 md:gap-x-5 lg:grid-cols-4">
                      {fallbackPicks.map((item) => (
                        <MovieCard key={item.id} item={item} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div>
              <div className="rounded-lg border border-background-200 bg-background-100 px-6 py-12 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                  <i className="ri-search-line text-2xl" />
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
                  输入关键词，开始查找
                </h2>
                <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground-500">
                  可以搜索片名、类型标签或主创姓名。上方的热门搜索也可以直接点开试试。
                </p>
              </div>

              <div className="mt-10">
                <div className="mb-5 flex items-end justify-between">
                  <h2 className="font-heading text-xl font-bold text-foreground-950">热度推荐</h2>
                  <Link
                    to="/category/tv"
                    className="text-xs text-foreground-500 transition-colors hover:text-primary-600"
                  >
                    更多剧集
                    <i className="ri-arrow-right-s-line ml-0.5 align-middle" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-7 sm:grid-cols-3 md:gap-x-5 lg:grid-cols-4">
                  {fallbackPicks.map((item) => (
                    <MovieCard key={item.id} item={item} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}